import { Box, CircularProgress, Snackbar, Stack, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import { BaseTheme, IconButton } from 'design-system';
import { getDownloadURL, ref, uploadBytesResumable, StorageReference, UploadTask } from 'firebase/storage';
import { useEffect, useState, useRef, ChangeEventHandler, forwardRef } from 'react';
import { MdDelete, MdDownload, MdEdit, MdOutlinePictureAsPdf } from 'react-icons/md';
import { auth, storage, qcStorage } from 'services/firebase';
import { v4 as uuidv4 } from 'uuid';
import { SnackbarVariant, useSnackbar } from 'hooks/useSnackBar';
import { ACCEPTED_FILE_TYPES, isAcceptedUpload, rejectedUploadMessage } from 'utils/acceptedUpload';
import { downloadFile } from 'utils/download-file';

const iconButtonStyle = (theme: BaseTheme) => ({
  color: theme.palette.common.white,
  backgroundColor: theme.palette.secondary.main,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
});

// Upload file via simple (non-resumable) upload with XHR progress tracking.
// Workaround for Firebase Storage resumable upload returning 503 on non-default buckets.
const simpleUploadWithProgress = (
  storageRef: StorageReference,
  file: File,
  customMetadata: Record<string, string>,
  onProgress: (progress: number) => void,
): Promise<string> =>
  new Promise((resolve, reject) => {
    const bucket = storageRef.bucket;
    const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o?name=${encodeURIComponent(storageRef.fullPath)}`;

    auth.currentUser
      ?.getIdToken()
      .then((token) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.setRequestHeader('Authorization', `Firebase ${token}`);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        };

        xhr.onload = async () => {
          if (xhr.status === 401 || xhr.status === 403) {
            reject(new Error('storage/unauthorized'));
            return;
          }
          if (xhr.status !== 200) {
            reject(new Error('storage/unknown'));
            return;
          }
          try {
            const response = JSON.parse(xhr.responseText);
            const metadataUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(response.name)}`;
            // Best-effort: file upload itself already succeeded; a failed
            // metadata patch shouldn't fail the user's save.
            try {
              await fetch(metadataUrl, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Firebase ${token}` },
                body: JSON.stringify({ metadata: customMetadata }),
              });
            } catch (metadataErr) {
              console.warn('[upload] failed to set metadata; continuing', metadataErr);
            }
            resolve(await getDownloadURL(storageRef));
          } catch (err) {
            reject(err instanceof Error ? err : new Error('storage/unknown'));
          }
        };

        xhr.onerror = () => reject(new Error('storage/unknown'));
        xhr.send(file);
      })
      .catch(() => reject(new Error('storage/unauthorized')));
  });

export const DEFAULT_UPLOAD_FOLDER = 'images/';

export const FileUploadPreview = ({
  src = '',
  rawFile,
  onComplete,
  isQCStorage = false,
  uploadFolder = DEFAULT_UPLOAD_FOLDER,
}: {
  src?: string;
  rawFile: File;
  onComplete(value: string): void;
  isQCStorage?: boolean;
  uploadFolder?: string;
}) => {
  const [progress, setProgress] = useState<null | number>(null);
  const [msg, setMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  // Parents pass a new onComplete each render; reading it from a ref keeps the upload tied to the file only
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    let isCancelled = false;
    let uploadTask: UploadTask | undefined;

    const handleUpload = async () => {
      try {
        setProgress(0);
        setUploading(true);
        const extension = rawFile.name.split('.').pop();
        const storageRef = ref(isQCStorage ? qcStorage : storage, `${uploadFolder}${uuidv4()}.${extension}`);
        const customMetadata = { originalName: rawFile.name ?? '', extension: extension ?? '' };

        if (isQCStorage) {
          const downloadURL = await simpleUploadWithProgress(storageRef, rawFile, customMetadata, setProgress);
          if (isCancelled) return;
          onCompleteRef.current(downloadURL);
          setUploading(false);
          setMsg('');
          return;
        }

        const task = uploadBytesResumable(storageRef, rawFile, { customMetadata });
        uploadTask = task;

        task.on(
          'state_changed',
          (snapshot) => {
            setProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
          },
          (error) => {
            if (isCancelled) return;
            switch (error.code) {
              case 'storage/unauthorized':
                setMsg('Uploading: unauthorized');
                break;
              case 'storage/canceled':
                setMsg('Uploading: canceled');
                break;
              case 'storage/unknown':
              default:
                setMsg('Failed to upload file');
                break;
            }
            setProgress(null);
          },
          async () => {
            const downloadURL = await getDownloadURL(task.snapshot.ref);
            if (isCancelled) return;
            onCompleteRef.current(downloadURL);
            setUploading(false);
            setMsg('');
          },
        );
      } catch (error) {
        if (isCancelled) return;
        const message = error instanceof Error ? error.message : '';
        if (message === 'storage/unauthorized') {
          setMsg('Uploading: unauthorized');
        } else {
          setMsg('Failed to upload file');
        }
        setProgress(null);
        setUploading(false);
      }
    };
    if (rawFile) handleUpload();

    return () => {
      isCancelled = true;
      uploadTask?.cancel();
    };
  }, [rawFile, isQCStorage, uploadFolder]);

  if (uploading || !src) {
    return (
      <Box
        sx={[
          {
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          (theme) => ({ margin: theme.spacing(1, 0) }),
        ]}
      >
        {msg && <Alert severity="error">{msg}</Alert>}
        {progress !== null && (
          <>
            <CircularProgress variant="determinate" value={progress} size={48} />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" component="div" color="textSecondary">{`${progress}%`}</Typography>
            </Box>
          </>
        )}
      </Box>
    );
  }

  return <img src={src} alt="uploading" />;
};
export const FileEdit = forwardRef(function FileEdit(
  {
    src,
    onChange,
    onRemove,
    uploadFolder,
    acceptedFileTypes = ACCEPTED_FILE_TYPES,
  }: {
    src: string;
    onChange: (value: string) => void;
    onRemove: () => void;
    uploadFolder?: string;
    acceptedFileTypes?: string;
  },
  ref,
) {
  const [showError, setShowError] = useState(false);
  const [isDownloading, setDownloading] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const { showSnackbar } = useSnackbar();

  const handleEditClick = () => {
    if (!inputRef.current) {
      return;
    }
    inputRef.current.click();
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await fetch(src);
      const contentType = response.headers.get('Content-Type');
      const fileType = contentType?.startsWith('image/') ? 'png' : 'pdf';
      await downloadFile(src, `${uuidv4()}.${fileType}`);
    } catch (error) {
      setShowError(true);
    } finally {
      setDownloading(false);
    }
  };

  const handleFileUpload: ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (!event.target.files || !event.target.files.length) {
      setFile(null);
      return;
    }
    if (!isAcceptedUpload(event.target.files[0], acceptedFileTypes)) {
      showSnackbar({ message: rejectedUploadMessage(acceptedFileTypes) }, SnackbarVariant.ERROR);
      event.target.value = '';
      return;
    }
    setFile(event.target.files[0]);
  };

  useEffect(() => {
    if (src) setShowFallback(false);
  }, [src]);

  return (
    <>
      <input
        accept={acceptedFileTypes}
        type="file"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
        ref={inputRef}
      />
      {src && (
        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.palette.grey[300]}`,
            backgroundColor: theme.palette.grey[100],
            borderRadius: theme.spacing(0.75),
            overflow: 'hidden',
            height: 160,
            position: 'relative',
            marginBottom: theme.spacing(1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.grey[700],
          })}
        >
          {showFallback ? (
            <MdOutlinePictureAsPdf size={40} />
          ) : (
            <Box
              ref={ref}
              component="img"
              sx={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center' }}
              src={src}
              alt=""
              onError={() => setShowFallback(true)}
            />
          )}
          <Stack
            direction="column"
            sx={[
              {
                justifyContent: 'space-between',
                gap: 0.5,
              },
              (theme) => ({
                position: 'absolute',
                right: theme.spacing(0.5),
                paddingTop: theme.spacing(0.5),
                paddingBottom: theme.spacing(0.5),
                height: '100%',
              }),
            ]}
          >
            <IconButton disabled={isDownloading} onClick={handleEditClick} sx={iconButtonStyle}>
              <MdEdit size={20} />
            </IconButton>
            <IconButton disabled={isDownloading} onClick={handleDownload} sx={iconButtonStyle}>
              <MdDownload size={20} />
            </IconButton>
            <IconButton
              disabled={isDownloading}
              onClick={onRemove}
              sx={(theme) => ({
                color: theme.palette.common.white,
                backgroundColor: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: theme.palette.error.dark,
                },
              })}
            >
              <MdDelete size={20} />
            </IconButton>
          </Stack>
        </Box>
      )}
      {file && <FileUploadPreview rawFile={file} onComplete={onChange} uploadFolder={uploadFolder} />}
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={showError}
        autoHideDuration={3000}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to download file
        </Alert>
      </Snackbar>
    </>
  );
});
